import type {
  IAccountingFees,
  IAccountingFeesDetails,
} from "../interfaces/models/IAccountingFees";
import { parseCalendarDate } from "./dates";
import { ServiceType } from "../Enums/FirmEnum";
import {
  DEFAULT_SPECIAL_FUND_NAME,
  DEFAULT_SPECIAL_FUND_PERCENTAGE,
  resolveSpecialFundFromDoc,
} from "./accountingSpecialFund";
import {
  DEFAULT_ANNUAL_MEETING_REG_FEE,
  resolveAnnualMeetingRegFeeFromDoc,
} from "./accountingAnnualMeetingFee";

const LEGACY_MEMBERSHIP_TYPE = "membershipLegalAndAccounting";

type ProfessionalsRange = NonNullable<
  IAccountingFeesDetails["numberOfProfessionals"]
>;

type MembershipDetailType = "membershipLegal" | "membershipAccounting";

const PROFESSIONALS_RANGES: { range: ProfessionalsRange; min: number; max: number }[] =
  [
    { range: "1-10", min: 1, max: 10 },
    { range: "11-20", min: 11, max: 20 },
    { range: "21-40", min: 21, max: 40 },
    { range: "41-60", min: 41, max: 60 },
    { range: "61-80", min: 61, max: 80 },
    { range: "81-100", min: 81, max: 100 },
    { range: "101-150", min: 101, max: 150 },
    { range: "151-200", min: 151, max: 200 },
    { range: "201-300", min: 201, max: 300 },
    { range: "301-400", min: 301, max: 400 },
    { range: "401-500", min: 401, max: 500 },
    { range: "501-600", min: 501, max: 600 },
    { range: "601-700", min: 601, max: 700 },
    { range: "701-800", min: 701, max: 800 },
    { range: "801-900", min: 801, max: 900 },
    { range: "901-1000", min: 901, max: 1000 },
    { range: "1000+", min: 1001, max: Number.POSITIVE_INFINITY },
  ];

const LEGAL_ACCOUNTING_SERVICE_TYPES = new Set([
  ServiceType.Legal,
  ServiceType.Accounting,
  ServiceType.Other,
  "Legal",
  "Legal Services",
  "Accounting",
  "legal",
  "accounting",
  "other",
  "Other",
]);

const INVESTMENT_BANKING_SERVICE_TYPES = new Set([
  ServiceType["Investment Banking"],
  "Investment Banking",
  "InvestmentBanking",
]);

/**
 * Resolves jurisdiction-size professional count for billing tiers.
 * Priority: `firmProfessionalsInPrimary` → `professionalsInPrimary` → legacy `countOfPeople`.
 */
export function parseProfessionalsCount(firm: {
  firmProfessionalsInPrimary?: string | number | null;
  professionalsInPrimary?: string | number | null;
  countOfPeople?: string | number | null;
}): number | null {
  const raw =
    firm.firmProfessionalsInPrimary ??
    firm.professionalsInPrimary ??
    firm.countOfPeople ??
    null;
  if (raw === null || raw === undefined || raw === "") {
    return null;
  }

  const parsed = Number(String(raw).replace(/[^0-9.]/g, ""));
  if (Number.isNaN(parsed) || parsed <= 0) {
    return null;
  }
  return parsed;
}

export function matchProfessionalsRange(
  count: number
): ProfessionalsRange | null {
  for (const entry of PROFESSIONALS_RANGES) {
    if (count >= entry.min && count <= entry.max) {
      return entry.range;
    }
  }
  return null;
}

export function isInitiationYear(
  dateJoined: string | Date | undefined | null,
  currentYear: number
): boolean {
  const joined = parseCalendarDate(dateJoined);
  if (!joined) {
    return false;
  }
  return joined.getUTCFullYear() === currentYear;
}

function isLegacyMembershipDetailType(type: string): boolean {
  return type === LEGACY_MEMBERSHIP_TYPE;
}

function isAccountingServiceType(serviceType: unknown): boolean {
  const normalized = normalizeServiceType(String(serviceType ?? "").trim());
  if (normalized === ServiceType.Accounting) {
    return true;
  }
  const lower = String(serviceType ?? "").trim().toLowerCase();
  return lower === "accounting";
}

export function resolveMembershipDetailType(
  serviceType: unknown,
): MembershipDetailType {
  return isAccountingServiceType(serviceType)
    ? "membershipAccounting"
    : "membershipLegal";
}

export function resolveLegalAccountingDetailType(
  isInitiation: boolean,
  serviceType?: unknown,
): IAccountingFeesDetails["type"] {
  if (isInitiation) {
    return "initiationLegalAndAccounting";
  }
  return resolveMembershipDetailType(serviceType);
}

type InvestmentBankingRange = NonNullable<
  IAccountingFeesDetails["investmentBanking"]
>;

const INVESTMENT_BANKING_RANGE_ORDER: InvestmentBankingRange[] = [
  "OneFee",
  "<=1mill",
  "1mill-4mill",
  "4mill-7mill",
  "7mill-10mill",
  "10mill-13mill",
  "13mill+",
];

const INVESTMENT_BANKING_RANGE_LABELS: Record<InvestmentBankingRange, string> =
  {
    OneFee: "One Fee",
    "<=1mill": "Less than $1 million",
    "1mill-4mill": "$1 million to $4 million",
    "4mill-7mill": "$4 million to $7 million",
    "7mill-10mill": "$7 million to $10 million",
    "10mill-13mill": "$10 million to $13 million",
    "13mill+": "More than $13 million",
  };

export interface InvoiceDuesTier {
  id: string;
  label: string;
  fee: number;
}

export interface InvoiceFeesResult {
  year: number | null;
  duesTiers: InvoiceDuesTier[];
  initiationByRange: Record<string, number>;
  defaultTierId: string;
  isInvestmentBanking: boolean;
  specialFundName: string;
  specialFundPercentage: number;
  annualMeetingRegFee: number;
}

export function isLegalOrAccountingFirm(firm: { serviceType?: unknown }): boolean {
  return isLegalOrAccountingService(firm.serviceType);
}

export function isInvestmentBankingFirm(firm: { serviceType?: unknown }): boolean {
  return isInvestmentBankingService(firm.serviceType);
}

export function parseFeesDocToInvoiceFees(
  firm: {
    serviceType?: unknown;
    firmProfessionalsInPrimary?: string | number | null;
    professionalsInPrimary?: string | number | null;
    countOfPeople?: string | number | null;
  },
  feesDoc: IAccountingFees | null | undefined,
): InvoiceFeesResult {
  const empty: InvoiceFeesResult = {
    year: feesDoc?.year ?? null,
    duesTiers: [],
    initiationByRange: {},
    defaultTierId: "",
    isInvestmentBanking: false,
    specialFundName: DEFAULT_SPECIAL_FUND_NAME,
    specialFundPercentage: DEFAULT_SPECIAL_FUND_PERCENTAGE,
    annualMeetingRegFee: DEFAULT_ANNUAL_MEETING_REG_FEE,
  };

  if (!feesDoc?.details?.length) {
    return empty;
  }

  const specialFund = resolveSpecialFundFromDoc(feesDoc);
  const annualMeetingRegFee = resolveAnnualMeetingRegFeeFromDoc(feesDoc);

  const scheduleServiceType = resolveFeesServiceType(firm.serviceType);

  if (isInvestmentBankingService(scheduleServiceType)) {
    const byRange = new Map<InvestmentBankingRange, number>();
    for (const detail of feesDoc.details) {
      if (
        detail.type !== "membershipInvestmentBanking" ||
        !detail.investmentBanking
      ) {
        continue;
      }
      byRange.set(detail.investmentBanking, Number(detail.fee) || 0);
    }

    const duesTiers = INVESTMENT_BANKING_RANGE_ORDER.filter((range) =>
      byRange.has(range),
    ).map((range) => ({
      id: range,
      label: INVESTMENT_BANKING_RANGE_LABELS[range],
      fee: byRange.get(range) ?? 0,
    }));

    return {
      year: feesDoc.year,
      duesTiers,
      initiationByRange: {},
      defaultTierId: byRange.has("OneFee")
        ? "OneFee"
        : (duesTiers[0]?.id ?? ""),
      isInvestmentBanking: true,
      ...specialFund,
      annualMeetingRegFee,
    };
  }

  return {
    ...buildLegalAccountingFeesResult(firm, feesDoc),
    ...specialFund,
    annualMeetingRegFee,
  };
}

function normalizeServiceType(serviceType: unknown): string {
  const raw = String(serviceType ?? "").trim();
  const aliases: Record<string, string> = {
    legal: ServiceType.Legal,
    accounting: ServiceType.Accounting,
    investmentBanking: ServiceType["Investment Banking"],
    other: ServiceType.Other,
    Legal: ServiceType.Legal,
    Accounting: ServiceType.Accounting,
    "Investment Banking": ServiceType["Investment Banking"],
    Other: ServiceType.Other,
  };
  return aliases[raw] ?? raw;
}

/** Other offices bill using the Legal Services fee schedule. */
export function resolveFeesServiceType(serviceType: unknown): string {
  const normalized = normalizeServiceType(serviceType);
  return normalized === ServiceType.Other ? ServiceType.Legal : normalized;
}

function isLegalOrAccountingService(serviceType: unknown): boolean {
  const scheduleType = resolveFeesServiceType(serviceType);
  const normalized = normalizeServiceType(scheduleType);
  return LEGAL_ACCOUNTING_SERVICE_TYPES.has(normalized);
}

function isInvestmentBankingService(serviceType: unknown): boolean {
  const normalized = normalizeServiceType(serviceType);
  return INVESTMENT_BANKING_SERVICE_TYPES.has(normalized);
}

function buildLegalAccountingFeesResult(
  firm: {
    serviceType?: unknown;
    firmProfessionalsInPrimary?: string | number | null;
    professionalsInPrimary?: string | number | null;
    countOfPeople?: string | number | null;
  },
  feesDoc: IAccountingFees,
): InvoiceFeesResult {
  const membershipType = resolveMembershipDetailType(firm.serviceType);
  const duesByRange = new Map<ProfessionalsRange, number>();
  const initiationByRange: Record<string, number> = {};

  for (const detail of feesDoc.details) {
    const range = detail.numberOfProfessionals;
    if (!range) {
      continue;
    }
    const fee = Number(detail.fee) || 0;
    const typeStr = String(detail.type);

    if (detail.type === "initiationLegalAndAccounting") {
      initiationByRange[range] = fee;
      continue;
    }

    if (detail.type === membershipType) {
      duesByRange.set(range, fee);
      continue;
    }

    if (isLegacyMembershipDetailType(typeStr) && !duesByRange.has(range)) {
      duesByRange.set(range, fee);
    }
  }

  const duesTiers = PROFESSIONALS_RANGES.filter((entry) =>
    duesByRange.has(entry.range),
  ).map((entry) => ({
    id: entry.range,
    label: `${entry.range} Professionals`,
    fee: duesByRange.get(entry.range) ?? 0,
  }));

  const professionalsCount = parseProfessionalsCount(firm);
  const matchedRange =
    professionalsCount !== null
      ? matchProfessionalsRange(professionalsCount)
      : null;
  const defaultTierId =
    matchedRange && duesByRange.has(matchedRange)
      ? matchedRange
      : (duesTiers[0]?.id ?? "");

  return {
    year: feesDoc.year,
    duesTiers,
    initiationByRange,
    defaultTierId,
    isInvestmentBanking: false,
    ...resolveSpecialFundFromDoc(feesDoc),
    annualMeetingRegFee: resolveAnnualMeetingRegFeeFromDoc(feesDoc),
  };
}

export function calculateAnnualDuesInvoiced(
  firm: {
    serviceType?: unknown;
    dateJoined?: string | Date | null;
    firmProfessionalsInPrimary?: string | number | null;
    professionalsInPrimary?: string | number | null;
  },
  feesDoc: IAccountingFees | null | undefined,
  currentYear: number = new Date().getFullYear()
): number | null {
  if (!feesDoc?.details?.length) {
    return null;
  }

  const serviceType = resolveFeesServiceType(firm.serviceType);

  if (isInvestmentBankingService(serviceType)) {
    const detail = feesDoc.details.find(
      (d) =>
        d.type === "membershipInvestmentBanking" &&
        d.investmentBanking === "OneFee"
    );
    return detail?.fee ?? null;
  }

  if (!isLegalOrAccountingService(serviceType)) {
    return null;
  }

  const professionalsCount = parseProfessionalsCount(firm);
  if (professionalsCount === null) {
    return null;
  }

  const professionalsRange = matchProfessionalsRange(professionalsCount);
  if (!professionalsRange) {
    return null;
  }

  const detailType = resolveLegalAccountingDetailType(
    isInitiationYear(firm.dateJoined, currentYear),
    firm.serviceType,
  );

  let detail = feesDoc.details.find(
    (d) =>
      d.type === detailType && d.numberOfProfessionals === professionalsRange,
  );

  if (!detail && detailType !== "initiationLegalAndAccounting") {
    detail = feesDoc.details.find(
      (d) =>
        String(d.type) === LEGACY_MEMBERSHIP_TYPE &&
        d.numberOfProfessionals === professionalsRange,
    );
  }

  return detail?.fee ?? null;
}

export const INVALID_PROFESSIONALS_COUNT_MESSAGE =
  "Please enter a valid number of professionals for this office before creating an invoice.";

export function resolveProfessionalsCountError(firm: {
  serviceType?: unknown;
  firmProfessionalsInPrimary?: string | number | null;
  professionalsInPrimary?: string | number | null;
  countOfPeople?: string | number | null;
}): string | null {
  if (isInvestmentBankingFirm(firm)) {
    return null;
  }

  const count = parseProfessionalsCount(firm);
  if (count === null || matchProfessionalsRange(count) === null) {
    return INVALID_PROFESSIONALS_COUNT_MESSAGE;
  }
  return null;
}
